import axios from 'axios';
// Example within an API call function (e.g., in src/api/snippets.js or wherever you fetch data)
import { supabase } from '../supabaseClient'; // Adjust path

const API_BASE = process.env.REACT_APP_TWIF_API_HOST;// Your backend URL
//const API_BASE = 'https://api.theweekinfantasy.com'; // Your backend URL
export const fetchProtectedData = async () => {
  try {
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();

    if (sessionError) {
      throw new Error(`Supabase session error: ${sessionError.message}`);
    }

    if (!session) {
      // This should ideally not happen if the user is on a protected route,
      // but handle it just in case.
      throw new Error("No active session found. Please log in.");
    }

    const token = session.access_token;

    const response = await fetch('/api/protected-data', { // Use your actual API endpoint URL
      method: 'GET', // or POST, PUT, etc.
      headers: {
        'Authorization': `Bearer ${token}`, // Send the token in the Authorization header
        'Content-Type': 'application/json'
        // Add other headers if needed
      },
      // body: JSON.stringify(data) // if sending data
    });

    if (!response.ok) {
       // Handle non-2xx responses (like 401 Unauthorized from backend)
       const errorData = await response.json().catch(() => ({ detail: response.statusText }));
       throw new Error(`API Error ${response.status}: ${errorData.detail || 'Failed to fetch data'}`);
    }

    return await response.json();

  } catch (error) {
    console.error("Failed to fetch protected data:", error);
    // Re-throw or handle the error appropriately in the UI
    throw error;
  }
};


/**
 * Fetches snippets for a specific player, optionally filtered by date range.
 * @param {string|number} playerId - The ID of the player.
 * @param {number|null} days - Number of past days (7, 30 etc.). Pass null or 0 for all time.
 * @returns {Promise<Array>} - A promise that resolves to an array of snippet objects.
 */
export const fetchSnippetsByPlayer = async (playerId, days = null) => { // Default days to null
  try {
    const params = {
        player_id: playerId,
    };
    // Add 'days' parameter ONLY if it's a positive number
    if (days !== null && days > 0) {
      params.days = days;
    }

    console.log(`Workspaceing snippets for player ${playerId} with params:`, params); // Log params

    const res = await axios.get(`${API_BASE}/snippets`, { params });
    return res.data || []; // Ensure return is always an array
  } catch (error) {
      console.error(`Error fetching snippets for player ${playerId}:`, error);
      // Handle specific errors if needed (e.g., 404)
      if (error.response && error.response.status === 404) {
          console.warn(`Player ${playerId} not found or has no snippets matching criteria.`);
          return []; // Return empty array for 404
      }
      throw error; // Re-throw other errors
  }
};

// Updated fetchPlayerData to include player summary
export const fetchPlayerData = async (playerId) => {
  try {
    // Fetch player details
    const playerDetailsResponse = await axios.get(`${API_BASE}/players`, {
      params: { id: playerId }
    });
    const playerData = playerDetailsResponse.data;

    // If player data is an array and not empty, proceed to fetch summary
    if (Array.isArray(playerData) && playerData.length > 0) {
      const player = playerData[0]; // Assuming the first element is the correct player

      // Fetch player summary
      try {
        const summaryResponse = await axios.get(`${API_BASE}/players/${playerId}/summary`);
        // Combine player data with summary; summary might be null if not found
        player.summary = summaryResponse.data || null; 
      } catch (summaryError) {
        // Log error if summary fetch fails, but don't let it break the player data fetch
        console.warn(`Could not fetch summary for player ${playerId}:`, summaryError);
        player.summary = null; // Ensure summary is null if fetch fails
      }
      return [player]; // Return as an array, consistent with previous structure
    } else {
      // If player not found or empty response, return original (likely empty) playerData
      return playerData;
    }
  } catch (error) {
    console.error(`Error fetching player data for player ${playerId}:`, error);
    throw error; // Re-throw error to be handled by the calling component
  }
};


/**
 * Searches for players by name.
 * Assumes the API supports filtering players via a 'name' query parameter.
 * Example API endpoint: GET /players?name=Josh%20A
 * @param {string} nameQuery - The partial or full name to search for.
 * @returns {Promise<Array>} - A promise that resolves to an array of player objects.
 */
export const searchPlayersByName = async (nameQuery) => {
  if (!nameQuery || nameQuery.trim().length < 3) {
    // Don't search if the query is too short
    return [];
  }
  try {
    const res = await axios.get(`${API_BASE}/players`, {
      params: {
        name: nameQuery // Pass the name query to the backend
      }
    });
    // Assuming the backend returns an array of players matching the query
    // Ensure the response data is an array, default to empty array if not
    return Array.isArray(res.data) ? res.data : [];
  } catch (error) {
    console.error("Error searching players:", error);
    // Return empty array on error to prevent crashes
    return [];
  }
  
};



// === UPDATE THE FOLLOWING FUNCTIONS ===

/**
 * Fetches top players grouped by classification.
 * @param {number} days - Number of past days (0 for all time).
 * @param {string | null} position - Position to filter by (e.g., 'QB', null for 'All').
 * @returns {Promise<object>}
 */
export const fetchTopPlayersByClassification = async (days = 30, position = null) => { // <-- Add position param
  try {
    const params = {};
    if (days > 0) params.days = days;
    if (position && position !== 'All') params.position = position; // <-- Add position to params

    console.log("API CALL: /top-players-by-classification, PARAMS:", params); // Debug log
    const res = await axios.get(`${API_BASE}/top-players-by-classification`, { params });
    return typeof res.data === 'object' && res.data !== null ? res.data : {};
  } catch (error) {
    console.error("Error fetching top players by classification:", error);
    throw error;
  }
};

/**
 * Fetches top 10 players overall by snippet count.
 * @param {number} days - Number of past days (0 for all time).
 * @param {string | null} position - Position to filter by (e.g., 'QB', null for 'All').
 * @returns {Promise<Array>}
 */
export const fetchTopPlayersOverall = async (days = 30, position = null) => { // <-- Add position param
  try {
    const params = {};
    if (days > 0) params.days = days;
    if (position && position !== 'All') params.position = position; // <-- Add position to params

    console.log("API CALL: /top-players-overall, PARAMS:", params); // Debug log
    const res = await axios.get(`${API_BASE}/top-players-overall`, { params });
    return Array.isArray(res.data) ? res.data : [];
  } catch (error) {
    console.error("Error fetching top players overall:", error);
    throw error;
  }
};

/**
 * Fetches top 10 players by snippet sentiment count.
 * @param {'Positive' | 'Negative'} sentiment - The sentiment type.
 * @param {number} days - Number of past days (0 for all time).
 * <param {string | null} position - Position to filter by (e.g., 'QB', null for 'All').
* @returns {Promise<Array>}
 */
export const fetchTopPlayersBySentiment = async (sentiment, days = 30, position = null) => { // <-- Add position param
  if (!sentiment) throw new Error("Sentiment parameter is required");
  try {
    const params = { sentiment }; // sentiment is required
    if (days > 0) params.days = days;
    if (position && position !== 'All') params.position = position; // <-- Add position to params

    console.log("API CALL: /top-players-by-sentiment, PARAMS:", params); // Debug log
    const res = await axios.get(`${API_BASE}/top-players-by-sentiment`, { params });
    return Array.isArray(res.data) ? res.data : [];
  } catch (error) {
    console.error(`Error fetching top players by ${sentiment} sentiment:`, error);
    throw error;
  }
};

/**
 * Fetches top 10 players by snippet value count.
 * @param {'High' | 'Medium' | 'Low'} value - The value type.
 * @param {number} days - Number of past days (0 for all time).
 * @param {string | null} position - Position to filter by (e.g., 'QB', null for 'All').
 * @returns {Promise<Array>}
 */
export const fetchTopPlayersByValue = async (value, days = 30, position = null) => { // <-- Add position param
  if (!['High', 'Medium', 'Low'].includes(value)) throw new Error("Invalid value parameter.");
  try {
    const params = { value }; // value is required
    if (days > 0) params.days = days;
    if (position && position !== 'All') params.position = position; // <-- Add position to params

    console.log("API CALL: /top-players-by-value, PARAMS:", params); // Debug log
    const res = await axios.get(`${API_BASE}/top-players-by-value`, { params });
    return Array.isArray(res.data) ? res.data : [];
  } catch (error) {
    console.error(`Error fetching top players by ${value} value:`, error);
    throw error;
  }
};

/**
 * Fetches all dashboard data in a single request for the MainMenuPage.
 * @param {number} days - Number of past days (0 for all time).
 * @param {string | null} position - Position to filter by (e.g., 'QB', null for 'All').
 * @returns {Promise<object>} - A promise that resolves to an object containing all dashboard data.
 */
export const fetchDashboardData = async (days = 7, position = null) => {
  try {
    const params = {};
    if (days > 0) params.days = days;
    if (position && position.toUpperCase() !== 'ALL') params.position = position;

    // console.log("API CALL: /dashboard-data, PARAMS:", params);
    const res = await axios.get(`${API_BASE}/dashboard-data`, { params });
    // Ensure a default structure if data is null or not as expected
    return res.data || {
      byClass: {},
      overall: [],
      positive: [],
      negative: [],
      highValue: []
    };
  } catch (error) {
    console.error("Error fetching dashboard data:", error);
    // Re-throw to be caught by the calling component, or handle with default structure
    // throw error;
    return { // Return default structure on error to prevent UI crashes
        byClass: {},
        overall: [],
        positive: [],
        negative: [],
        highValue: []
      };
  }
};
